//-----------------------------------------------------------------------
// Copyright © 2022 ManpowerGroup. All Rights Reserved.
//-----------------------------------------------------------------------

/* eslint-disable prettier/prettier */
import React, { useState, useEffect, Fragment } from "react";
import { useRoutes } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import ErrorHandler from "./errorHandler";
import { Context } from "./common/context/Context";
import {
  AuthenticatedTemplate,
  useMsal,
  useIsAuthenticated,
} from '@azure/msal-react';
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { loginRequest } from "./common/helpers/AuthService";
import "./i18n";
import routes from "./routes";
import jwt_decode from "jwt-decode";
import { ThemeProvider } from "./contexts/ThemeContext";
import { SidebarProvider } from "./contexts/SidebarContext";
import { LayoutProvider } from "./contexts/LayoutContext";
import ChartJsDefaults from "./utils/ChartJsDefaults";

import { ErrorBoundary } from "react-error-boundary";
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin, AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { createBrowserHistory } from "history";
import { signOut } from "./common/signout/signout";

import RoutesComp from "./aAdminTabTeam2/routes/RoutesComp";

import { clearAuthData } from "./aAdminTabTeam2/hooksAndFunctions/functions/clearAuthData";
import useCallSiteCoreContent from "./aAdminTabTeam2/common/utilities/sitecore/useCallSiteCoreContent";


var reactPlugin = new ReactPlugin();
const browserHistory = createBrowserHistory({ basename: '' });
var appInsights = new ApplicationInsights({
  config: {
    instrumentationKey: process.env.REACT_APP_INSIGHT_KEY,
    extensions: [reactPlugin],
    enableAutoRouteTracking: false,
    disableAjaxTracking: true,
    extensionConfig: {
      [reactPlugin.identifier]: { history: browserHistory }
    }
  }
});




/** */
appInsights.loadAppInsights();

const App = () => {

  useCallSiteCoreContent()


  const { instance, accounts } = useMsal();

  const isAuthenticated = useIsAuthenticated();
  const content = useRoutes(routes);
  const [redirectToDashboard, setRedirectToDashboard] = useState(false);
  const [programStatusList, setProgramStatusList] = useState([]);
  const [cascadeClientData, setCascadeClientData] = useState([]);
  const [rtPersona, setRTPersona] = useState([]);
  const [rtProgramClass, setRTProgramClass] = useState([]);
  const [rtProgramTemplate, setRTProgramTemplate] = useState([]);
  const [serviceOffice, setServiceOffice] = useState([]);
  const [countryList, setCountryList] = useState([]);
  const [rightOfficeId, setRightOfficeId] = useState([]);
  const [viewGATableData, setViewGATableData] = useState([]);
  const [manageClientsTableData, setManageClientsTableData] = useState([]);
  const [adminUser, setAdminUser] = useState(true);
  const [isUserActive, setIsUserActive] = useState(true);
  const [userAccountData, setUserAccountData] = useState();
  const [usersEmailList, setUsersEmailList] = useState([]);
  const [permissionsList, setPermissionsList] = useState([]);
  const [userDetails, setUserDetails] = useState();
  const [userRoleCountryList, setUserRoleCountryList] = useState();
  const [adminNavigationCmsData, setAdminNavigationCmsData] = useState({ title: "", description: "", tabNavigationTitle: "", pageContent: {} });
  let location;

  const context = {
    isUserActive,
    setIsUserActive,
    programStatusList,
    setProgramStatusList,
    cascadeClientData,
    setCascadeClientData,
    rtPersona,
    setRTPersona,
    rtProgramClass,
    setRTProgramClass,
    rtProgramTemplate,
    setRTProgramTemplate,
    serviceOffice,
    setServiceOffice,
    countryList,
    setCountryList,
    rightOfficeId,
    setRightOfficeId,
    viewGATableData,
    setViewGATableData,
    manageClientsTableData,
    setManageClientsTableData,
    adminUser,
    setAdminUser,
    userAccountData,
    setUserAccountData,
    usersEmailList,
    setUsersEmailList,
    permissionsList,
    setPermissionsList,
    userDetails,
    setUserDetails,
    location,
    userRoleCountryList,
    setUserRoleCountryList,
    adminNavigationCmsData,
    setAdminNavigationCmsData
  };

  useEffect(() => {
    if (!isAuthenticated) {
      instance.loginRedirect(loginRequest);
    }
  });

  const storeAccessToken = (accessTokenResponse) => {
    window.sessionStorage.setItem(
      "AccessToken",
      accessTokenResponse?.accessToken
    );
    const userAccount = {
      name: accessTokenResponse?.account?.name,
      email: accessTokenResponse?.account?.username,
      accountId: accessTokenResponse?.account?.localAccountId,
    };
    window.sessionStorage.setItem(
      "UserAccount",
      JSON.stringify(userAccount)
    );

    let startTime = jwt_decode(accessTokenResponse?.accessToken).exp - 300;
    let endTime = Date.now() / 1000;
    let diff = startTime - endTime;
    let refreshTime = diff * 1000;
    refreshTokenHandler(refreshTime)
  }
  let refreshTokenTimer;
  const refreshTokenHandler = (refreshTime) => {
    refreshTokenTimer = setTimeout(() => {
      refreshTokenAction();
    }, refreshTime);
  };
  const refreshTokenAction = () => {

    instance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0]
    }).then((accessTokenResponse) => {
      let startTime = jwt_decode(accessTokenResponse.accessToken).exp;
      let endTime = Date.now() / 1000;
      let diff = startTime - endTime;
      let refreshTime = diff * 1000;

      window.sessionStorage.setItem("AccessToken", accessTokenResponse.accessToken);
      refreshTokenHandler(refreshTime)
    })
  };


  const acquireADToken = () => {
    if (accounts && accounts.length) {
      instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0]
      }).then((accessTokenResponse) => {
        storeAccessToken(accessTokenResponse);
      })
        .catch((error) => {
          if (error instanceof InteractionRequiredAuthError) {
            console.log('auth error', error);
            instance.acquireTokenRedirect(loginRequest).then((accessTokenResponse) => {
              storeAccessToken(accessTokenResponse);
            })
          }
          console.log(error);
        });
    }
  };

  useEffect(() => {
    acquireADToken();
  }, [instance, accounts]);

  /**if user is idle for 30min then logout automatically. */
  // const { isIdle, remainingTime } = useUserIdleTimer()

  // useEffect(() => {
  //   if (!!isAuthenticated && !!isIdle) {
  //     signOut();
  //     clearAuthData()
  //   }
  // }, [isAuthenticated, isIdle])

  const events = [
    "load",
    "mousemove",
    "mousedown",
    "click",
    "scroll",
    "keypress",
  ];

  let idleTimer30 = 1800000;
  let idleTimer;
  useEffect(() => {
    Object.values(events).forEach((item) => {
      window.addEventListener(item, () => {
        resetIdleTimer();
        idleTimerHandler();
      });
    });
  }, []);
  const resetIdleTimer = () => {
    if (idleTimer) clearTimeout(idleTimer);
  };
  const idleTimerHandler = () => {
    idleTimer = setTimeout(() => {
      resetIdleTimer();
      Object.values(events).forEach((item) => {
        window.removeEventListener(item, resetIdleTimer);
      });
      signOut();
    }, idleTimer30);
  };

  /**if not authenticated clearinf auth data from local storage */
  useEffect(() => {
    if (!isAuthenticated) {
      clearAuthData()
    }
  }, [isAuthenticated])

  return (
    <ErrorBoundary FallbackComponent={ErrorHandler} key={location}>
      <HelmetProvider>
        <Helmet
          titleTemplate="Cirrus Admin"
          defaultTitle="Cirrus Admin"
        />
        <AppInsightsContext.Provider value={reactPlugin}>
          <Context.Provider value={context}>
            <ThemeProvider>
              <SidebarProvider>
                <LayoutProvider>
                  <ChartJsDefaults />
                  <Fragment>
                    {/* <AuthenticatedTemplate>{content}</AuthenticatedTemplate> */}

                    <AuthenticatedTemplate><RoutesComp /></AuthenticatedTemplate>
                    {/* <RoutesComp /> */}
                  </Fragment>
                </LayoutProvider>
              </SidebarProvider>
            </ThemeProvider>
          </Context.Provider>
        </AppInsightsContext.Provider>
      </HelmetProvider>
    </ErrorBoundary>
  );
};

export default App;
